import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Section, Field, useBusy } from "./common";
import { UserAvatar } from "./UserAvatar";

export function PhotoBox() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const ref = useRef();
  const [busy, run] = useBusy();
  const upload = (f) => f && run(async () => {
    const fd = new FormData();
    fd.append("file", f);
    try { const { data } = await api.post("/me/photo", fd); setUser(data); toast.success(t("Photo updated", "Foto actualizada")); }
    catch (e) { toast.error(errMsg(e)); } finally { ref.current.value = ""; }
  });
  const remove = async () => { const { data } = await api.delete("/me/photo"); setUser(data); toast.success(t("Photo removed", "Foto eliminada")); };
  return (
    <Section title={t("Profile photo", "Foto de perfil")} testid="profile-photo-section">
      <div className="p-4 flex items-center gap-4 flex-wrap">
        <UserAvatar user={user} size="w-20 h-20 text-2xl" testid="profile-photo" />
        <input ref={ref} type="file" hidden accept="image/png,image/jpeg,image/webp" onChange={(e) => upload(e.target.files[0])} data-testid="profile-photo-input" />
        <button className="btn-outline" disabled={busy} onClick={() => ref.current.click()} data-testid="profile-photo-upload-btn">
          <Camera className="w-5 h-5" /> {user.photo_v ? t("Change photo", "Cambiar foto") : t("Add photo", "Agregar foto")}
        </button>
        {user.photo_v && <button className="btn-ghost text-red-600" onClick={remove} data-testid="profile-photo-remove-btn"><Trash2 className="w-4 h-4" /> {t("Remove", "Quitar")}</button>}
      </div>
    </Section>
  );
}

const initForm = (u) => {
  const parts = (u.name || "").trim().split(/\s+/);
  const legacy = !u.first_name && !u.last_name;
  return { first_name: legacy ? parts[0] || "" : u.first_name || "", last_name: legacy ? parts.slice(1).join(" ") : u.last_name || "",
    display_name: u.display_name || "", phone: u.phone || "", job_title: u.job_title || "", lang: u.lang || "en" };
};

export function PersonalForm() {
  const { t, setLang } = useLang();
  const { user, setUser } = useAuth();
  const [f, setF] = useState(() => initForm(user));
  const [busy, run] = useBusy();
  const ch = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = (e) => run(async () => {
    e.preventDefault();
    try { const { data } = await api.put("/me/profile", f); setUser(data); setLang(data.lang, false); toast.success(t("Profile saved", "Perfil guardado")); }
    catch (err) { toast.error(errMsg(err)); }
  });
  const fields = [["first_name", t("First name", "Nombre")], ["last_name", t("Last name", "Apellido")], ["display_name", t("Display name (optional)", "Nombre para mostrar (opcional)")], ["phone", t("Phone number", "Teléfono")], ["job_title", t("Job title / position", "Cargo / puesto")]];
  return (
    <Section title={t("Personal information", "Información personal")} testid="profile-personal-section">
      <form onSubmit={save} className="p-4 grid sm:grid-cols-2 gap-3">
        {fields.map(([k, l]) => <Field key={k} label={l}><input className="inp" value={f[k]} maxLength={80} type={k === "phone" ? "tel" : "text"} onChange={ch(k)} data-testid={`profile-${k.replace("_", "-")}`} /></Field>)}
        <Field label={t("Preferred language", "Idioma preferido")}>
          <div className="grid grid-cols-2 gap-2">{["en", "es"].map((l) => <button type="button" key={l} onClick={() => setF({ ...f, lang: l })} className={f.lang === l ? "btn-dark" : "btn-outline"} data-testid={`profile-lang-${l}`}>{l === "en" ? "English" : "Español"}</button>)}</div>
        </Field>
        <button className="btn-primary sm:col-span-2 w-full sm:w-fit" disabled={busy} data-testid="profile-save-btn">{t("Save profile", "Guardar perfil")}</button>
      </form>
    </Section>
  );
}

export function EmailForm() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ new_email: "", current_password: "" });
  const [busy, run] = useBusy();
  const save = (e) => run(async () => {
    e.preventDefault();
    try { const { data } = await api.put("/me/email", f); setUser(data); setF({ new_email: "", current_password: "" }); toast.success(t("Email changed. Use it next time you sign in.", "Correo cambiado. Úselo la próxima vez que inicie sesión.")); }
    catch (err) { toast.error(errMsg(err)); }
  });
  return (
    <Section title={t("Sign-in email", "Correo de inicio de sesión")} testid="profile-email-section">
      <form onSubmit={save} className="p-4 grid gap-3">
        <div className="text-sm">{t("Current email", "Correo actual")}: <b data-testid="profile-current-email">{user.email}</b></div>
        <Field label={t("New email", "Nuevo correo")}><input className="inp" type="email" required value={f.new_email} onChange={(e) => setF({ ...f, new_email: e.target.value })} data-testid="profile-new-email" /></Field>
        <Field label={t("Current password (to confirm)", "Contraseña actual (para confirmar)")}><input className="inp" type="password" required autoComplete="current-password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="profile-email-password" /></Field>
        <button className="btn-dark w-full sm:w-fit" disabled={busy} data-testid="profile-email-save-btn">{t("Change email", "Cambiar correo")}</button>
      </form>
    </Section>
  );
}
